import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Search, FileText, CheckCircle,
  MinusCircle, PlusCircle, AlertTriangle, Send, Save, Scale,
  RefreshCw, Lock, Trash2, Plus, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const REASON_OPTIONS = [
  { value: 'price_error', label: 'Pricing error' },
  { value: 'quantity_error', label: 'Quantity error' },
  { value: 'service_cancellation', label: 'Service not rendered / cancellation' },
  { value: 'additional_charge', label: 'Additional charge' },
  { value: 'others', label: 'Others (specify in reason)' },
];

const AdjustmentCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const preselectedInvoiceId = searchParams.get('invoice_id');

  const [step, setStep] = useState(1);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('');
  const [refundType, setRefundType] = useState('partial');
  const [amount, setAmount] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [reason, setReason] = useState('');
  const [pmsRef, setPmsRef] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [existingAdjustments, setExistingAdjustments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const token = localStorage.getItem('einvoice_token');
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fmt = (v) => new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(v || 0);

  // Load edit data
  useEffect(() => {
    if (editId) {
      setEditMode(true);
      (async () => {
        try {
          const res = await fetch(`${API}/api/einvoice/gateway/adjustments/${editId}`, { headers: authHeaders });
          if (!res.ok) throw new Error();
          const adj = await res.json();
          setAdjustmentType(adj.adjustment_type);
          setRefundType(adj.refund_type || 'partial');
          setAmount(String(adj.amount));
          setReasonCategory(adj.reason_category || 'others');
          setReason(adj.reason || '');
          setPmsRef(adj.pms_reference_id || '');
          if (adj.line_items?.length) setLineItems(adj.line_items);
          // Load original invoice
          const invRes = await fetch(`${API}/api/einvoice/gateway/adjustments/search-invoices?q=${encodeURIComponent(adj.original_invoice_number)}`, { headers: authHeaders });
          if (invRes.ok) {
            const invData = await invRes.json();
            const found = invData.invoices?.find(i => i.id === adj.original_invoice_id);
            if (found) setSelectedInvoice(found);
          }
          setStep(3); // Jump to form step
        } catch {
          toast.error('Failed to load adjustment for editing');
        }
      })();
    }
  }, [editId]);

  // Preselect invoice
  useEffect(() => {
    if (preselectedInvoiceId && !editId) {
      (async () => {
        try {
          const res = await fetch(`${API}/api/einvoice/gateway/adjustments/search-invoices?q=`, { headers: authHeaders });
          if (res.ok) {
            const data = await res.json();
            const found = data.invoices?.find(i => i.id === preselectedInvoiceId);
            if (found) {
              setSelectedInvoice(found);
              setStep(2);
            }
          }
        } catch { /* ignore */ }
      })();
    }
  }, [preselectedInvoiceId]);

  // Search invoices
  const searchInvoices = useCallback(async (q) => {
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/adjustments/search-invoices?q=${encodeURIComponent(q)}`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.invoices || []);
      }
    } catch { /* ignore */ } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (invoiceSearch.length >= 1) searchInvoices(invoiceSearch); }, 300);
    return () => clearTimeout(t);
  }, [invoiceSearch, searchInvoices]);

  // Load on mount for preselection
  useEffect(() => { if (!editId && !preselectedInvoiceId) searchInvoices(''); }, []);

  // Fetch existing adjustments when invoice selected
  useEffect(() => {
    if (selectedInvoice?.id) {
      (async () => {
        try {
          const res = await fetch(`${API}/api/einvoice/gateway/adjustments/by-invoice/${selectedInvoice.id}`, { headers: authHeaders });
          if (res.ok) {
            const data = await res.json();
            setExistingAdjustments(data.adjustments || []);
          }
        } catch { /* ignore */ }
      })();
    }
  }, [selectedInvoice?.id]);

  const originalTotal = parseFloat(selectedInvoice?.total_amount || 0);
  const existingCnTotal = existingAdjustments
    .filter(a => a.adjustment_type === 'credit_note' && !['rejected', 'cancelled'].includes(a.status))
    .reduce((s, a) => s + (a.amount || 0), 0);
  const remainingForCn = originalTotal - existingCnTotal;
  const parsedAmount = parseFloat(amount) || 0;

  const effectiveTotal = adjustmentType === 'credit_note'
    ? originalTotal - parsedAmount
    : originalTotal + parsedAmount;

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]);
  };
  const removeLineItem = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const updateLineItem = (i, field, val) => {
    const updated = [...lineItems];
    updated[i] = { ...updated[i], [field]: field === 'description' ? val : parseFloat(val) || 0 };
    setLineItems(updated);
  };

  // Calculate total from line items
  useEffect(() => {
    if (lineItems.length > 0) {
      const total = lineItems.reduce((s, li) => s + (li.quantity * li.unit_price), 0);
      if (total > 0) setAmount(total.toFixed(2));
    }
  }, [lineItems]);

  const canProceedStep1 = !!selectedInvoice;
  const canProceedStep2 = !!adjustmentType;
  const canProceedStep3 = parsedAmount > 0 && reason.trim().length > 0 && reasonCategory;

  const handleFullRefund = () => {
    setRefundType('full');
    setAmount(remainingForCn.toFixed(2));
  };

  const handleSubmit = async (saveAsDraft = false) => {
    setSubmitting(true);
    try {
      const payload = {
        original_invoice_id: selectedInvoice.id,
        adjustment_type: adjustmentType,
        reason: reason.trim(),
        reason_category: reasonCategory,
        amount: parsedAmount,
        pms_reference_id: pmsRef || null,
      };
      if (adjustmentType === 'credit_note') payload.refund_type = refundType;
      if (lineItems.length > 0) {
        payload.line_items = lineItems.map(li => ({
          description: li.description || 'Adjustment',
          quantity: li.quantity || 1,
          unit_price: li.unit_price || 0,
          tax_rate: li.tax_rate || 0,
        }));
      }

      let res;
      if (editMode && editId) {
        res = await fetch(`${API}/api/einvoice/gateway/adjustments/${editId}`, {
          method: 'PUT', headers: authHeaders, body: JSON.stringify({
            reason: payload.reason, reason_category: payload.reason_category,
            amount: payload.amount, pms_reference_id: payload.pms_reference_id,
            refund_type: payload.refund_type, line_items: payload.line_items,
          })
        });
      } else {
        res = await fetch(`${API}/api/einvoice/gateway/adjustments`, {
          method: 'POST', headers: authHeaders, body: JSON.stringify(payload)
        });
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      const result = await res.json();

      if (!saveAsDraft && !editMode) {
        // Submit to LHDN immediately
        try {
          const submitRes = await fetch(`${API}/api/einvoice/gateway/adjustments/${result.id}/submit-lhdn`, {
            method: 'POST', headers: authHeaders
          });
          const submitData = await submitRes.json();
          if (submitRes.ok && submitData.status !== 'rejected') {
            toast.success(`${result.adjustment_number} submitted to LHDN`);
          } else {
            toast.warning(`${result.adjustment_number} saved. LHDN: ${submitData.error?.message || submitData.detail || 'Check status'}`);
          }
        } catch {
          toast.info(`${result.adjustment_number} saved as draft. Submit to LHDN from the list.`);
        }
      } else {
        toast.success(`${result.adjustment_number || 'Adjustment'} saved as draft`);
      }

      navigate('/einvoice/portal/adjustment');
    } catch (e) {
      toast.error(e.message || 'Failed to save adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = ['Select Invoice', 'Choose Type', 'Adjustment Details', 'Summary'];

  return (
    <PortalLayout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="adjustment-create-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/einvoice/portal/adjustment')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-gray-100 flex items-center gap-2">
              <Scale className="w-5 h-5 text-purple-600" />
              {editMode ? 'Edit Adjustment' : 'Create Adjustment'}
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400">e-Invoice Adjustment (LHDN)</p>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-start gap-2" data-testid="create-warning-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
            <p className="font-semibold">This adjustment is for invoices already validated by LHDN.</p>
            <p>This action will create a Credit Note or Debit Note for tax compliance. It does <strong>NOT</strong> modify the original invoice.</p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-1" data-testid="step-indicator">
          {stepLabels.map((label, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                step === i + 1
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : step > i + 1
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-gray-500'
              }`}>
                {step > i + 1 ? <CheckCircle className="w-3 h-3" /> : <span>{i + 1}</span>}
                <span className="hidden sm:inline">{label}</span>
              </div>
              {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${step > i + 1 ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-slate-200 dark:bg-gray-700'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Invoice */}
        {step === 1 && (
          <Card className="dark:bg-[#1a1a2e]" data-testid="step-1">
            <CardHeader>
              <CardTitle className="text-base">Step 1: Select Original Invoice</CardTitle>
              <p className="text-xs text-slate-500 dark:text-gray-400">Only LHDN-validated invoices are shown</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by Invoice No or Customer Name..."
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  className="pl-10"
                  data-testid="invoice-search"
                />
              </div>
              {searching && <p className="text-xs text-slate-400 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Searching...</p>}
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {searchResults.length === 0 && !searching && (
                  <p className="text-sm text-slate-400 text-center py-8">No valid invoices found. Only LHDN-validated invoices can be adjusted.</p>
                )}
                {searchResults.map(inv => (
                  <div
                    key={inv.id}
                    onClick={() => setSelectedInvoice(inv)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedInvoice?.id === inv.id
                        ? 'border-purple-400 bg-purple-50/50 dark:bg-purple-950/20 dark:border-purple-600'
                        : 'border-slate-200 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-800'
                    }`}
                    data-testid={`invoice-option-${inv.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono text-sm font-medium">{inv.invoice_number}</span>
                        <span className="text-xs text-slate-400 ml-2">{inv.guest_name}</span>
                        {inv.source_collection === 'owner_payments' && <Badge className="ml-2 text-[10px] bg-amber-100 text-amber-700">Owner Payment</Badge>}
                        {inv.has_adjustment && <Badge className="ml-2 text-[10px] bg-purple-100 text-purple-600">Has adjustments</Badge>}
                      </div>
                      <div className="text-right">
                        <span className="font-mono font-bold text-sm">{fmt(inv.total_amount)}</span>
                        <Badge className="ml-2 text-[10px] bg-emerald-100 text-emerald-700">VALID</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Choose Type */}
        {step === 2 && (
          <Card className="dark:bg-[#1a1a2e]" data-testid="step-2">
            <CardHeader>
              <CardTitle className="text-base">Step 2: Choose Adjustment Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setAdjustmentType('credit_note')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    adjustmentType === 'credit_note'
                      ? 'border-red-400 bg-red-50 dark:bg-red-950/20 dark:border-red-600'
                      : 'border-slate-200 dark:border-gray-700 hover:border-red-200'
                  }`}
                  data-testid="type-credit-note"
                >
                  <MinusCircle className={`w-8 h-8 mb-3 ${adjustmentType === 'credit_note' ? 'text-red-500' : 'text-slate-400'}`} />
                  <p className="font-bold text-red-700 dark:text-red-400">Credit Note (CN)</p>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Reduce / Refund — InvoiceTypeCode: 02</p>
                  <p className="text-[11px] text-red-500 dark:text-red-400 mt-2 font-medium">This CN will reduce revenue</p>
                </button>
                <button
                  onClick={() => setAdjustmentType('debit_note')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    adjustmentType === 'debit_note'
                      ? 'border-green-400 bg-green-50 dark:bg-green-950/20 dark:border-green-600'
                      : 'border-slate-200 dark:border-gray-700 hover:border-green-200'
                  }`}
                  data-testid="type-debit-note"
                >
                  <PlusCircle className={`w-8 h-8 mb-3 ${adjustmentType === 'debit_note' ? 'text-green-500' : 'text-slate-400'}`} />
                  <p className="font-bold text-green-700 dark:text-green-400">Debit Note (DN)</p>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Add Charge / Underbilling — InvoiceTypeCode: 03</p>
                  <p className="text-[11px] text-green-500 dark:text-green-400 mt-2 font-medium">This DN will increase revenue</p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Details Form (split LEFT/RIGHT layout) */}
        {step === 3 && selectedInvoice && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="step-3">
            {/* LEFT: Original Invoice Snapshot (READ-ONLY) */}
            <Card className="dark:bg-[#1a1a2e]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Original Invoice (Read-Only)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-400">Invoice No</Label>
                    <p className="font-mono font-medium">{selectedInvoice.invoice_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Amount</Label>
                    <p className="font-mono font-bold text-lg">{fmt(originalTotal)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Customer</Label>
                    <p>{selectedInvoice.guest_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">LHDN UUID</Label>
                    <p className="font-mono text-xs text-slate-400 break-all">{selectedInvoice.lhdn_uuid}</p>
                  </div>
                  {selectedInvoice.guest_tin && (
                    <div>
                      <Label className="text-xs text-slate-400">Buyer TIN</Label>
                      <div className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-slate-300" />
                        <p className="font-mono text-xs">{selectedInvoice.guest_tin}</p>
                      </div>
                    </div>
                  )}
                  {selectedInvoice.booking_ref && (
                    <div>
                      <Label className="text-xs text-slate-400">PMS Booking Ref</Label>
                      <p className="text-xs">{selectedInvoice.booking_ref}</p>
                    </div>
                  )}
                </div>

                {/* Existing adjustments */}
                {existingAdjustments.length > 0 && (
                  <div className="border-t dark:border-gray-700 pt-3 mt-3">
                    <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">Adjustment History ({existingAdjustments.length})</p>
                    {existingAdjustments.map(a => (
                      <div key={a.id} className="flex items-center justify-between text-xs py-1 border-b last:border-0 border-slate-100 dark:border-gray-800">
                        <div className="flex items-center gap-1.5">
                          <Badge className={`text-[10px] ${a.adjustment_type === 'credit_note' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                            {a.adjustment_type === 'credit_note' ? 'CN' : 'DN'}
                          </Badge>
                          <span className="font-mono">{a.adjustment_number}</span>
                        </div>
                        <span className={`font-mono ${a.adjustment_type === 'credit_note' ? 'text-red-600' : 'text-green-600'}`}>
                          {a.adjustment_type === 'credit_note' ? '-' : '+'}{fmt(a.amount)}
                        </span>
                      </div>
                    ))}
                    {adjustmentType === 'credit_note' && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Remaining adjustable: {fmt(remainingForCn)}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* RIGHT: Adjustment Input */}
            <Card className="dark:bg-[#1a1a2e]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {adjustmentType === 'credit_note'
                    ? <><MinusCircle className="w-4 h-4 text-red-500" /> Credit Note Details</>
                    : <><PlusCircle className="w-4 h-4 text-green-500" /> Debit Note Details</>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Adjustment Method (CN only) */}
                {adjustmentType === 'credit_note' && (
                  <div>
                    <Label className="text-xs">Adjustment Method</Label>
                    <div className="flex gap-2 mt-1">
                      <Button variant={refundType === 'full' ? 'default' : 'outline'} size="sm"
                        onClick={handleFullRefund} className={refundType === 'full' ? 'bg-red-600 hover:bg-red-700' : ''}
                        data-testid="method-full">Full Adjustment</Button>
                      <Button variant={refundType === 'partial' ? 'default' : 'outline'} size="sm"
                        onClick={() => { setRefundType('partial'); setAmount(''); }}
                        className={refundType === 'partial' ? 'bg-red-600 hover:bg-red-700' : ''}
                        data-testid="method-partial">Partial Adjustment</Button>
                    </div>
                  </div>
                )}

                {/* Line Items (delta-based) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Adjustment Line Items</Label>
                    <Button variant="outline" size="sm" onClick={addLineItem} className="h-6 text-xs" data-testid="add-line-item">
                      <Plus className="w-3 h-3 mr-1" /> Add Line
                    </Button>
                  </div>
                  {lineItems.length > 0 ? (
                    <div className="space-y-2">
                      {lineItems.map((li, i) => (
                        <div key={i} className="grid grid-cols-[1fr_60px_80px_32px] gap-2 items-end" data-testid={`line-item-${i}`}>
                          <div>
                            {i === 0 && <Label className="text-[10px] text-slate-400">Description</Label>}
                            <Input value={li.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                              placeholder="Item description" className="h-8 text-xs" />
                          </div>
                          <div>
                            {i === 0 && <Label className="text-[10px] text-slate-400">Qty</Label>}
                            <Input type="number" min="1" value={li.quantity} onChange={(e) => updateLineItem(i, 'quantity', e.target.value)}
                              className="h-8 text-xs" />
                          </div>
                          <div>
                            {i === 0 && <Label className="text-[10px] text-slate-400">Unit Price</Label>}
                            <Input type="number" step="0.01" min="0" value={li.unit_price} onChange={(e) => updateLineItem(i, 'unit_price', e.target.value)}
                              className="h-8 text-xs font-mono" />
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeLineItem(i)} className="h-8 w-8 text-red-400 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* Amount */}
                <div>
                  <Label className="text-xs">{adjustmentType === 'credit_note' ? 'Reduction Amount (MYR)' : 'Additional Amount (MYR)'} *</Label>
                  <Input type="number" step="0.01" min="0.01"
                    max={adjustmentType === 'credit_note' ? remainingForCn : undefined}
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00" disabled={adjustmentType === 'credit_note' && refundType === 'full'}
                    className="font-mono text-lg" data-testid="amount-input" />
                  {adjustmentType === 'credit_note' && parsedAmount > remainingForCn && (
                    <p className="text-xs text-red-500 mt-1">Exceeds remaining adjustable ({fmt(remainingForCn)})</p>
                  )}
                </div>

                {/* Reason Category */}
                <div>
                  <Label className="text-xs">Reason Category *</Label>
                  <Select value={reasonCategory} onValueChange={setReasonCategory}>
                    <SelectTrigger data-testid="reason-category">
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {REASON_OPTIONS.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Reason Detail */}
                <div>
                  <Label className="text-xs">Reason Detail (Mandatory) *</Label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder={adjustmentType === 'credit_note' ? 'e.g. Overcharged for 2 nights, guest checked out early...' : 'e.g. Minibar charges not included in original invoice...'}
                    className="w-full min-h-[70px] rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                    data-testid="reason-input" />
                </div>

                {/* PMS Reference */}
                <div>
                  <Label className="text-xs text-slate-400">PMS Booking / Folio Reference (Optional)</Label>
                  <Input value={pmsRef} onChange={(e) => setPmsRef(e.target.value)} placeholder="e.g. BK-12345" data-testid="pms-ref" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Summary */}
        {step === 4 && selectedInvoice && (
          <Card className="dark:bg-[#1a1a2e]" data-testid="step-4">
            <CardHeader>
              <CardTitle className="text-base">Summary — Review Before Submission</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Original */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Original Invoice</p>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Invoice No</span>
                      <span className="font-mono font-medium">{selectedInvoice.invoice_number}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Customer</span>
                      <span>{selectedInvoice.guest_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Amount</span>
                      <span className="font-mono font-bold">{fmt(originalTotal)}</span>
                    </div>
                  </div>
                </div>

                {/* Right: Adjustment */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Adjustment</p>
                  <div className={`rounded-lg p-4 space-y-2 ${adjustmentType === 'credit_note' ? 'bg-red-50 dark:bg-red-950/20' : 'bg-green-50 dark:bg-green-950/20'}`}>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Type</span>
                      <Badge className={adjustmentType === 'credit_note' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                        {adjustmentType === 'credit_note' ? 'Credit Note (CN)' : 'Debit Note (DN)'}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Adjustment Amount</span>
                      <span className={`font-mono font-bold ${adjustmentType === 'credit_note' ? 'text-red-600' : 'text-green-600'}`}>
                        {adjustmentType === 'credit_note' ? '-' : '+'}{fmt(parsedAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Reason</span>
                      <span className="text-right text-xs max-w-[200px]">{REASON_OPTIONS.find(r => r.value === reasonCategory)?.label}: {reason}</span>
                    </div>
                    {pmsRef && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">PMS Ref</span>
                        <span>{pmsRef}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Financial Impact */}
              <div className="bg-slate-900 dark:bg-slate-800 text-white rounded-lg p-4" data-testid="financial-impact">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-300">Original Amount</span>
                  <span className="font-mono">{fmt(originalTotal)}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-300">Adjustment</span>
                  <span className={`font-mono font-bold ${adjustmentType === 'credit_note' ? 'text-red-400' : 'text-green-400'}`}>
                    {adjustmentType === 'credit_note' ? '-' : '+'}{fmt(parsedAmount)}
                  </span>
                </div>
                <hr className="border-slate-700 my-2" />
                <div className="flex items-center justify-between">
                  <span className="font-semibold">New Effective Total</span>
                  <span className="font-mono text-xl font-bold text-purple-300">{fmt(effectiveTotal)}</span>
                </div>
                <p className={`text-xs mt-2 ${adjustmentType === 'credit_note' ? 'text-red-400' : 'text-green-400'}`}>
                  {adjustmentType === 'credit_note'
                    ? `This CN will reduce revenue by ${fmt(parsedAmount)}`
                    : `This DN will increase revenue by ${fmt(parsedAmount)}`}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2">
          <div>
            {step > 1 && !editMode && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="prev-step-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/einvoice/portal/adjustment')}>Cancel</Button>
            {step < 4 && !editMode && (
              <Button onClick={() => setStep(s => s + 1)}
                disabled={step === 1 ? !canProceedStep1 : step === 2 ? !canProceedStep2 : !canProceedStep3}
                className="bg-purple-600 hover:bg-purple-700" data-testid="next-step-btn">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {(step === 4 || editMode) && (
              <>
                <Button variant="outline" onClick={() => handleSubmit(true)} disabled={submitting || (step === 3 && !canProceedStep3)} data-testid="save-draft-btn">
                  <Save className="w-4 h-4 mr-1" /> Save as Draft
                </Button>
                <Button onClick={() => handleSubmit(false)}
                  disabled={submitting || (step === 3 && !canProceedStep3)}
                  className={adjustmentType === 'credit_note' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                  data-testid="submit-lhdn-btn">
                  {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit to LHDN
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default AdjustmentCreatePage;
